import { useEffect, useState } from "react";
import { usePlay } from "@/providers/play-provider";
import { useAuth } from "@/providers/auth-provider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users } from "lucide-react";
import { showMessageBox } from "@/components/blocks/message-box";
import {
  GameMetadata,
  getGameMetadata,
} from "@/pages/subs/play/common/game-registry";
import { RoomHeader } from "./room-header";
import { RoomHostMenu } from "./room-host-menu";
import { RoomPlayers } from "./room-players";

export const RoomView = () => {
  const { user } = useAuth();
  const { currentRoom, startGame } = usePlay();
  const [gameMetadata, setGameMetadata] = useState<GameMetadata | null>(null);

  useEffect(() => {
    if (currentRoom) {
      const metadata = getGameMetadata(currentRoom.gameType);
      setGameMetadata(metadata || null);
    }
  }, [currentRoom?.gameType]);

  if (!currentRoom) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-muted-foreground">게임방 정보를 불러오는 중...</p>
      </div>
    );
  }

  const isHost = currentRoom.hostId === user?._id;
  const canStartGame =
    isHost &&
    currentRoom.status === "waiting" &&
    currentRoom.players.length >= (gameMetadata?.minPlayers || 2);

  const handleStartGame = async () => {
    const result = await showMessageBox({
      type: "Confirm_Alert",
      title: "게임 시작",
      description: "게임을 시작하시겠습니까?",
    });

    if (result === "Yes") {
      startGame();
    }
  };

  const renderGameContent = () => {
    if (currentRoom.status === "waiting") {
      return (
        <div className="text-center py-8">
          <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-semibold mb-2">
            플레이어를 기다리는 중...
          </h3>
          <p className="text-muted-foreground mb-4">
            게임을 시작하려면 최소 {gameMetadata?.minPlayers || 2}명의
            플레이어가 필요합니다.
          </p>
        </div>
      );
    }

    if (currentRoom.status === "playing") {
      // 메타데이터가 없으면 기본 메시지
      return (
        <div className="text-center py-8">
          <h3 className="text-xl font-semibold mb-4">게임이 진행 중입니다!</h3>
          <div className="bg-muted p-6 rounded-lg">
            <p className="text-muted-foreground">게임을 불러오는 중...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">게임이 완료되었습니다.</p>
      </div>
    );
  };

  return (
    <div className="w-full h-full p-4">
      <div className="max-w-4xl mx-auto">
        <RoomHeader />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>게임 화면</CardTitle>
              </CardHeader>
              <CardContent>{renderGameContent()}</CardContent>
            </Card>
          </div>

          <div>
            <RoomPlayers room={currentRoom} />

            {isHost && currentRoom.status === "waiting" && gameMetadata && (
              <RoomHostMenu
                game={gameMetadata}
                canStartGame={canStartGame}
                handleStartGame={handleStartGame}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
